/**
 * 効果音の音作り（すべて WebAudio による合成音／音源ファイル不要）。
 *
 * 素材を一切使わないため著作権上の懸念がなく、
 * レベルやコンボ数に応じて音程・音色を動的に変えられるのが利点。
 */

import { envelope, type AudioBus } from './AudioEngine';
import {
  comboNoteHz,
  dropNoteHz,
  IN_SCALE_SEMITONES,
  mergeNoteHz,
  scaleSemitone,
  semitoneToHz,
} from './scale';

interface PluckOptions {
  hz: number;
  at: number;
  gain: number;
  decay: number;
  /** 倍音の混ぜ具合（0〜1）。大きいほど固く明るい音。 */
  bright?: number;
  type?: OscillatorType;
}

/** 琴・マリンバのような撥弦／打鍵音。 */
function pluck(bus: AudioBus, options: PluckOptions): void {
  const { ctx, out } = bus;
  const { hz, at, gain, decay } = options;
  const bright = options.bright ?? 0.35;

  const amp = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(Math.min(12000, hz * 8 + 900), at);
  filter.frequency.exponentialRampToValueAtTime(Math.max(220, hz * 1.6), at + decay);
  filter.Q.value = 0.7;

  const osc = ctx.createOscillator();
  osc.type = options.type ?? 'triangle';
  osc.frequency.setValueAtTime(hz, at);

  const harmonic = ctx.createOscillator();
  harmonic.type = 'sine';
  harmonic.frequency.setValueAtTime(hz * 2.01, at);
  const harmonicGain = ctx.createGain();
  harmonicGain.gain.value = bright * 0.5;

  envelope(amp, at, gain, 0.004, decay);

  osc.connect(amp);
  harmonic.connect(harmonicGain).connect(amp);
  amp.connect(filter).connect(out);

  osc.start(at);
  harmonic.start(at);
  osc.stop(at + decay + 0.08);
  harmonic.stop(at + decay + 0.08);
}

interface ThumpOptions {
  from: number;
  to: number;
  at: number;
  gain: number;
  decay: number;
}

/** 太鼓・着地のような低音。 */
function thump(bus: AudioBus, options: ThumpOptions): void {
  const { ctx, out } = bus;
  const { from, to, at, gain, decay } = options;
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(from, at);
  osc.frequency.exponentialRampToValueAtTime(Math.max(30, to), at + decay * 0.8);

  const amp = ctx.createGain();
  envelope(amp, at, gain, 0.005, decay);

  osc.connect(amp).connect(out);
  osc.start(at);
  osc.stop(at + decay + 0.05);
}

interface NoiseOptions {
  at: number;
  gain: number;
  decay: number;
  freq: number;
  q?: number;
  type?: BiquadFilterType;
}

/** ノイズ系（アタックの「コッ」やシェイカー）。 */
function noiseHit(bus: AudioBus, options: NoiseOptions): void {
  const { ctx, out, noise } = bus;
  const { at, gain, decay, freq } = options;

  const source = ctx.createBufferSource();
  source.buffer = noise;
  source.loop = true;

  const filter = ctx.createBiquadFilter();
  filter.type = options.type ?? 'bandpass';
  filter.frequency.value = freq;
  filter.Q.value = options.q ?? 1.1;

  const amp = ctx.createGain();
  envelope(amp, at, gain, 0.002, decay);

  source.connect(filter).connect(amp).connect(out);
  source.start(at);
  source.stop(at + decay + 0.05);
}

/* --------------------------------- 効果音 --------------------------------- */

/** 投下音。大きい料理ほど低く重い「ぽん」。 */
export function playDropSfx(bus: AudioBus, level: number): void {
  const at = bus.ctx.currentTime;
  const hz = dropNoteHz(level);
  thump(bus, { from: hz * 2.2, to: hz, at, gain: 0.22, decay: 0.12 });
  noiseHit(bus, { at, gain: 0.05, decay: 0.045, freq: 1800, q: 0.8 });
}

/**
 * 合体音。生成レベルに応じて音階を1段ずつ上げる。
 * 上位ほど余韻を長く、低音を足して「重み」を出す。
 */
export function playMergeSfx(bus: AudioBus, level: number): void {
  const at = bus.ctx.currentTime;
  const hz = mergeNoteHz(level);
  const weight = Math.min(1, Math.max(0, (level - 2) / 10));
  const decay = 0.24 + weight * 0.5;

  noiseHit(bus, { at, gain: 0.045, decay: 0.03, freq: 2600, q: 0.9 });
  pluck(bus, { hz, at, gain: 0.26, decay, bright: 0.5 - weight * 0.2 });
  // 5度上を薄く重ねて響きを豊かに
  pluck(bus, { hz: hz * 1.4983, at: at + 0.012, gain: 0.1, decay: decay * 0.8, bright: 0.3 });

  if (level >= 6) {
    thump(bus, { from: hz * 0.52, to: hz * 0.34, at, gain: 0.14 + weight * 0.1, decay: 0.22 });
  }
  // Lv9 以上は鈴のような高い倍音を重ねて「特別」を耳でも分かるように
  if (level >= 9) {
    pluck(bus, { hz: hz * 2, at: at + 0.04, gain: 0.09, decay: decay * 1.2, bright: 1, type: 'sine' });
    pluck(bus, { hz: hz * 3.01, at: at + 0.07, gain: 0.05, decay: decay * 1.3, bright: 1, type: 'sine' });
  }
}

/**
 * ぶつかった音。強さで音量、料理の大きさで音程（大きいほど低い）。
 * 木の器に当たるような短い「こつ」。合体音の邪魔をしないよう小さく。
 */
export function playImpactSfx(bus: AudioBus, level: number, strength: number): void {
  const at = bus.ctx.currentTime;
  const s = Math.min(1, Math.max(0, strength));
  const hz = dropNoteHz(level) * 2.4;
  noiseHit(bus, { at, gain: 0.02 + s * 0.05, decay: 0.035, freq: 900 + (12 - level) * 120, q: 1.4 });
  thump(bus, { from: hz * 1.6, to: hz, at, gain: 0.04 + s * 0.08, decay: 0.07 + s * 0.04 });
}

/**
 * 最上位の一品ができたとき。太鼓2発と、陽音階の和音を駆け上がる。
 */
export function playTopSfx(bus: AudioBus): void {
  const at = bus.ctx.currentTime;
  thump(bus, { from: 150, to: 55, at, gain: 0.34, decay: 0.45 });
  thump(bus, { from: 150, to: 55, at: at + 0.22, gain: 0.3, decay: 0.5 });
  noiseHit(bus, { at, gain: 0.08, decay: 0.2, freq: 500, q: 0.6, type: 'lowpass' });
  for (let i = 0; i < 9; i += 1) {
    pluck(bus, {
      hz: semitoneToHz(scaleSemitone([0, 2, 4, 7, 9], i + 3)),
      at: at + 0.3 + i * 0.06,
      gain: 0.17,
      decay: 0.6,
      bright: 0.8,
      type: 'sine',
    });
  }
  // 最後に和音で締める
  [10, 12, 14].forEach((step) =>
    pluck(bus, {
      hz: semitoneToHz(scaleSemitone([0, 2, 4, 7, 9], step)),
      at: at + 0.9,
      gain: 0.14,
      decay: 1.4,
      bright: 0.6,
    }),
  );
}

/** コンボ音。合体音に上ハモリと余韻を重ねる。 */
export function playComboSfx(bus: AudioBus, level: number, combo: number): void {
  playMergeSfx(bus, level);
  const at = bus.ctx.currentTime;
  const lift = comboNoteHz(level, combo);
  pluck(bus, { hz: lift, at: at + 0.06, gain: 0.18, decay: 0.34, bright: 0.7, type: 'sine' });
  pluck(bus, { hz: lift * 2, at: at + 0.12, gain: 0.08, decay: 0.4, bright: 0.9, type: 'sine' });
}

/** 最高レベル同士のボーナス。上昇アルペジオ。 */
export function playBonusSfx(bus: AudioBus): void {
  const at = bus.ctx.currentTime;
  for (let i = 0; i < 6; i += 1) {
    const hz = semitoneToHz(scaleSemitone([0, 2, 4, 7, 9], i + 5));
    pluck(bus, {
      hz,
      at: at + i * 0.055,
      gain: 0.2 - i * 0.012,
      decay: 0.4,
      bright: 0.8,
      type: 'sine',
    });
  }
  thump(bus, { from: 160, to: 60, at, gain: 0.24, decay: 0.36 });
}

/** 図鑑の新規解放。短いきらめき。 */
export function playUnlockSfx(bus: AudioBus): void {
  const at = bus.ctx.currentTime;
  [7, 9, 12].forEach((step, i) => {
    pluck(bus, {
      hz: semitoneToHz(scaleSemitone([0, 2, 4, 7, 9], step)),
      at: at + i * 0.07,
      gain: 0.16,
      decay: 0.42,
      bright: 0.9,
      type: 'sine',
    });
  });
}

/** ゲームオーバー。陰音階で下降。 */
export function playGameOverSfx(bus: AudioBus): void {
  const at = bus.ctx.currentTime;
  [6, 4, 2, 0].forEach((step, i) => {
    pluck(bus, {
      hz: semitoneToHz(scaleSemitone(IN_SCALE_SEMITONES, step)) / 2,
      at: at + i * 0.14,
      gain: 0.2,
      decay: 0.55,
      bright: 0.2,
    });
  });
  thump(bus, { from: 110, to: 44, at: at + 0.02, gain: 0.3, decay: 0.7 });
  noiseHit(bus, { at: at + 0.02, gain: 0.06, decay: 0.5, freq: 420, q: 0.6, type: 'lowpass' });
}
